import React from 'react';
import { ArrowUpDown, ChevronDown, ChevronRight, MessageSquareText, Banknote, CheckCheck, Paperclip } from 'lucide-react';
import { formatCurrency } from '../../../utils/format';
import { formatDateForGroup } from '../../../utils/date';
import api from '../../../services/api';
import type { SortConfig } from '../../../hooks/useSort';
import type { PurchaseOrder, PurchaseOrderItem } from '../../../types/purchaseOrder';
import type { GreenBeanProfile } from '../../../types/greenBean';
import { useLanguage } from '../../../features/language/useLanguage';
import { POStatusBadge } from './table/POStatusBadge';
import { POLineItemsPanel } from './table/POLineItemsPanel';
import { PaymentProofPreviewModal } from './table/PaymentProofPreviewModal';

interface PurchaseOrderTableProps {
  filteredPOs: PurchaseOrder[];
  shouldGroup: boolean;
  groupedPOs: Record<string, PurchaseOrder[]>;
  sortedDates: string[];
  sortConfig: SortConfig<PurchaseOrder> | null;
  onSort: (key: keyof PurchaseOrder) => void;
  currencySymbol: string;
  expandedRowIds: Set<string>;
  onToggleRow: (id: string, event: React.MouseEvent) => void;
  onEdit: (po: PurchaseOrder) => void;
  onSupplierClick: (supplierId: string) => void;
  onPay: (po: PurchaseOrder) => void;
  onReceive: (po: PurchaseOrder) => void;
  onProfileClick: (item: PurchaseOrderItem) => void;
  greenBeanProfiles: GreenBeanProfile[];
  canWrite?: boolean;
}

export const PurchaseOrderTable: React.FC<PurchaseOrderTableProps> = ({
  filteredPOs,
  shouldGroup,
  groupedPOs,
  sortedDates,
  sortConfig,
  onSort,
  currencySymbol,
  expandedRowIds,
  onToggleRow,
  onEdit,
  onSupplierClick,
  onPay,
  onReceive,
  onProfileClick,
  greenBeanProfiles,
  canWrite = true,
}) => {
  const { t } = useLanguage();
  const [selectedProofPath, setSelectedProofPath] = React.useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = React.useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = React.useState<string | null>(null);

  const isImage = (path: string | null | undefined) => {
    if (!path) return false;
    const ext = path.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '');
  };

  const openPreview = async (path: string) => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }

    setSelectedProofPath(path);
    setIsPreviewModalOpen(true);

    if (isImage(path)) {
      try {
        const filename = path.split('/').pop() || path;
        const response = await api.get(`/uploads/documents/${filename}`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([response.data]));
        setPreviewBlobUrl(url);
      } catch (err) {
        console.error('Failed to fetch preview', err);
      }
    }
  };

  React.useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  const handleDownload = async (path: string) => {
    try {
      const filename = path.split('/').pop() || path;
      const response = await api.get(`/uploads/documents/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert(t('purchaseOrder.paymentDetails.downloadFailed'));
    }
  };

  // Image proofs open inline; other file types download directly.
  const handleProofAction = (path: string) => {
    if (isImage(path)) {
      openPreview(path);
    } else {
      handleDownload(path);
    }
  };

  const renderSortIndicator = (key: keyof PurchaseOrder) =>
    sortConfig?.key === key ? (
      <ArrowUpDown className="h-3.5 w-3.5 text-primary-600" />
    ) : (
      <ArrowUpDown className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
    );

  const headerCell = (key: keyof PurchaseOrder, label: string, sortable = true) => (
    <th
      className={`group px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
        sortable ? 'cursor-pointer hover:bg-gray-100/70 transition-colors' : ''
      }`}
      onClick={sortable ? () => onSort(key) : undefined}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {sortable && renderSortIndicator(key)}
      </div>
    </th>
  );

  const renderRow = (po: PurchaseOrder) => {
    const isExpanded = expandedRowIds.has(po.id);
    const paymentStatus = po.paymentStatus || 'unpaid';
    const deliveryStatus = po.deliveryStatus || 'pending';

    return (
      <React.Fragment key={po.id}>
        <tr
          data-test-id={`po-row-${po.id}`}
          onClick={() => onEdit(po)}
          className="group/row hover:bg-primary-50/30 cursor-pointer transition-colors"
        >
          <td className="px-4 py-3.5 whitespace-nowrap text-sm font-semibold text-gray-900 tabular-nums">
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-test-id={`po-expand-button-${po.id}`}
                onClick={(event) => onToggleRow(po.id, event)}
                className="p-1 text-gray-400 hover:text-primary-600 rounded-md hover:bg-primary-50 transition-colors"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <span>{po.orderNumber}</span>
              {po.internalNotes && (
                <div className="group relative" onClick={(e) => e.stopPropagation()}>
                  <MessageSquareText className="h-4 w-4 text-blue-500" />
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-50 transition-all duration-200 break-words whitespace-pre-wrap">
                    {po.internalNotes}
                    <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                  </div>
                </div>
              )}
              {po.paymentProofUrl && (
                <div
                  className="group relative"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isImage(po.paymentProofUrl)) {
                      openPreview(po.paymentProofUrl!);
                    } else {
                      handleDownload(po.paymentProofUrl!);
                    }
                  }}
                >
                  <Paperclip
                    className={`h-4 w-4 ${
                      isImage(po.paymentProofUrl)
                        ? 'text-primary-500 hover:text-primary-600'
                        : 'text-green-500 hover:text-green-600'
                    }`}
                  />
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-50 transition-all duration-200">
                    {t('purchaseOrder.paymentDetails.proofOfPayment')}
                    <div className="text-[10px] mt-1 text-gray-400">
                      {isImage(po.paymentProofUrl) ? t('purchaseOrder.actions.viewProof') : t('purchaseOrder.actions.downloadProof')}
                    </div>
                    <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                  </div>
                </div>
              )}
            </div>
          </td>
          <td className="px-4 py-3.5 whitespace-nowrap text-sm">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onSupplierClick(po.supplier);
              }}
              className="text-primary-700 hover:text-primary-800 hover:underline font-medium text-left"
            >
              {po.supplierName || t('settingsAdminMembers.unknownCompany')}
            </button>
          </td>
          <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-900 tabular-nums">
            {new Date(po.orderDate).toLocaleDateString()}
          </td>
          <td className="px-4 py-3.5 whitespace-nowrap text-sm font-medium text-gray-900 tabular-nums">
            {formatCurrency(po.totalAmount, currencySymbol)}
          </td>
          <td className="px-4 py-3.5 whitespace-nowrap">
            <POStatusBadge kind="order" status={po.status} />
          </td>
          <td className="px-4 py-3.5 whitespace-nowrap">
            <div className="flex items-center gap-2">
              <POStatusBadge kind="payment" status={paymentStatus} />
              {paymentStatus === 'unpaid' && po.status !== 'cancelled' && canWrite && (
                <button
                  type="button"
                  data-test-id={`po-pay-button-${po.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onPay(po);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100 ring-1 ring-inset ring-emerald-600/10 transition-colors"
                >
                  <Banknote className="h-4 w-4" />
                  {t('dashboard.outstandingPaymentsModal.markAsPaid')}
                </button>
              )}
            </div>
          </td>
          <td className="px-4 py-3.5 whitespace-nowrap">
            <div className="flex items-center gap-2">
              <POStatusBadge kind="delivery" status={deliveryStatus} />
              {deliveryStatus !== 'received' && po.status !== 'cancelled' && canWrite && (
                <button
                  type="button"
                  data-test-id={`po-receive-button-${po.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onReceive(po);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 ring-1 ring-inset ring-blue-600/10 transition-colors"
                >
                  <CheckCheck className="h-4 w-4" />
                  {t('purchaseOrder.form.submit.receive')}
                </button>
              )}
            </div>
          </td>
        </tr>
        {isExpanded && (
          <tr className="bg-gray-50/70">
            <td colSpan={7} className="px-4 py-4">
              <POLineItemsPanel
                po={po}
                currencySymbol={currencySymbol}
                greenBeanProfiles={greenBeanProfiles}
                canWrite={canWrite}
                isImage={isImage}
                onProofAction={handleProofAction}
                onProfileClick={onProfileClick}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div
      className="hidden md:block bg-white shadow-sm sm:rounded-lg border border-gray-200 overflow-hidden"
      data-test-id="po-table"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50/80 backdrop-blur-sm sticky top-0 z-10">
            <tr>
              {headerCell('orderNumber', t('purchaseOrder.table.orderNumberShort'))}
              {headerCell('supplier', t('purchasing.sampleList.table.supplier'))}
              {headerCell('orderDate', t('reports.general.table.date'))}
              {headerCell('totalAmount', t('reports.details.total'))}
              {headerCell('status', t('purchaseOrder.table.orderStatus'))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                {t('purchaseOrder.table.paymentStatus')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                {t('purchaseOrder.table.deliveryStatus')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredPOs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                  {t('purchaseOrder.table.empty')}
                </td>
              </tr>
            ) : shouldGroup ? (
              sortedDates.map((dateKey) => (
                <React.Fragment key={dateKey}>
                  <tr className="bg-gradient-to-r from-primary-50/60 via-primary-50/30 to-transparent">
                    <td colSpan={7} className="px-4 py-2 text-xs font-semibold text-primary-800 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                        {formatDateForGroup(dateKey)}
                      </span>
                    </td>
                  </tr>
                  {groupedPOs[dateKey].map(renderRow)}
                </React.Fragment>
              ))
            ) : (
              filteredPOs.map(renderRow)
            )}
          </tbody>
        </table>
      </div>

      <PaymentProofPreviewModal
        isOpen={isPreviewModalOpen}
        proofPath={selectedProofPath}
        blobUrl={previewBlobUrl}
        isImage={isImage}
        onClose={() => setIsPreviewModalOpen(false)}
        onDownload={handleDownload}
      />
    </div>
  );
};
